import type { ShapeNameConversionDisplayMap } from '@mbfab/shared'
import { assignSummaryPageNumbers, convertShapeNameForDisplay, isSome } from '@mbfab/shared'
import dayjs from 'dayjs'
import { sortBy, uniq } from 'es-toolkit'

import type { SteelOrderForPrint } from '../print-types'

import type { ColumnConfig } from './svg/main-row'
import { columnTypeLabel } from './svg/main-row'
import type { Item } from './svg/millsheet-list-svg'

export type SteelOrderExportRow = { constructionName: string; issuedDate: string; item: Item }

// プレビュー・実ダウンロードの両方が共有する列定義（1列 = ラベル + 行からの値取り出し）。
// この形にまとめておくことで、右ペインの擬似プレビューと実際にダウンロードされる CSV / Excel の
// 列構成・値が「同じ定義から作られている」ことをコード上で保証する（2箇所に手書きしてズレる事故を防ぐ）
export type SteelOrderExportPreviewColumn = {
  key: string
  label: string
  getValue: (row: SteelOrderExportRow) => string
}

// CSV（発注・ミルシートデータ）の列は Excel と異なり「表示項目」選択の対象外で、常に固定の列を出力する
const CSV_COLUMNS: SteelOrderExportPreviewColumn[] = [
  { key: 'referenceNumber', label: '参照番号', getValue: (row) => row.item.referenceNumber ?? '' },
  { key: 'pageNumber', label: 'ページ番号', getValue: (row) => String(row.item.pageNumber ?? '') },
  { key: 'no', label: '証明書番号', getValue: (row) => row.item.no ?? '' },
  { key: 'manufacturerName', label: 'メーカー', getValue: (row) => row.item.manufacturerName ?? '' },
  { key: 'tradingCompany', label: '商社', getValue: (row) => row.item.tradingCompany ?? '' },
  { key: 'customer', label: '需要家', getValue: (row) => row.item.customer ?? '' },
  { key: 'constructionName', label: '工事名称', getValue: (row) => row.constructionName },
  { key: 'issuedDate', label: '発行日', getValue: (row) => row.issuedDate },
  { key: 'orderNumber', label: '注文番号', getValue: (row) => row.item.orderNumber ?? '' },
  { key: 'sectionType', label: '形状', getValue: (row) => row.item.sectionType ?? '' },
  { key: 'steelType', label: '規格', getValue: (row) => row.item.steelType ?? '' },
  { key: 'size', label: '寸法', getValue: (row) => row.item.size ?? '' },
  { key: 'length', label: '長さ', getValue: (row) => row.item.length ?? '' },
  { key: 'quantity', label: '数量', getValue: (row) => row.item.quantity ?? '' },
  { key: 'weight', label: '重量', getValue: (row) => row.item.weight ?? '' },
  { key: 'heatNumber', label: '製鋼番号', getValue: (row) => row.item.heatNumber ?? '' },
]

export function csvExportPreviewColumns(): SteelOrderExportPreviewColumn[] {
  return CSV_COLUMNS
}

// Excel（使用鋼材一覧表）の列は「表示項目」で選んだ active な ColumnConfig をそのまま使う
// （downloadMillsheetListExcel に渡すものと同じ配列）。ラベルは実際の Excel ヘッダー行と同じ
// columnTypeLabel を使う（print-columns.ts の printColumnTypeLabel は表示項目選択 UI 専用のラベルで、
// 「商社」→「商社名」のように実際のヘッダー表記と異なるため、プレビューでは使わない）
export function excelExportPreviewColumns(activeColumns: ColumnConfig[]): SteelOrderExportPreviewColumn[] {
  return activeColumns.map((column) => ({
    key: column.type,
    label: columnTypeLabel[column.type],
    getValue: (row) => String(row.item[column.type] ?? ''),
  }))
}

export type SteelOrderExportGroup = { constructionName: string; rows: SteelOrderExportRow[] }

// 使用鋼材一覧表（Excel）を工事名称ごとに分割出力するためのグルーピング。
// ページ番号（主番-枝番）は buildSteelOrderExportRows が選択した発注全体を通して採番しているため、
// ここでは行の中身・pageNumber には一切手を入れず、出現順（＝集計表の印刷順）を保ったまま束ねるだけにする。
// Object.entries ベースの groupBy だと、工事名称が数字だけの文字列（"10" 等）のとき
// 挿入順ではなく数値昇順に並び替わってしまうため、配列ベースの uniq + filter で組む
export function groupSteelOrderExportRowsByConstruction(rows: SteelOrderExportRow[]): SteelOrderExportGroup[] {
  return uniq(rows.map((row) => row.constructionName)).map((constructionName) => ({
    constructionName,
    rows: rows.filter((row) => row.constructionName === constructionName),
  }))
}

export function buildSteelOrderExportRows(
  orders: SteelOrderForPrint[],
  shapeNameConversionMap: ShapeNameConversionDisplayMap,
  pageNumberByMillsheetId?: ReadonlyMap<string, number | string>,
): SteelOrderExportRow[] {
  const entries = orders.flatMap((order) =>
    sortBy(order.millsheets, [(millsheet) => millsheet.no]).map((millsheet) => ({
      referenceNumber: order.referenceNumber,
      supplierName: order.supplierName,
      millsheet,
    })),
  )
  const orderedEntries = uniq(entries.map((entry) => entry.millsheet.constructionName)).flatMap((constructionName) =>
    entries.filter((entry) => entry.millsheet.constructionName === constructionName),
  )
  const numberings = assignSummaryPageNumbers(
    orderedEntries.map((entry) => Math.max(entry.millsheet.millsheetItems.length, 1)),
  )

  return orderedEntries.flatMap(({ referenceNumber, supplierName, millsheet }, millsheetIndex) => {
    const detailItems = millsheet.millsheetItems.length === 0 ? [undefined] : millsheet.millsheetItems
    return detailItems.map((item, itemIndex) => ({
      constructionName: millsheet.constructionName,
      issuedDate: millsheet.issuedDate,
      item: {
        sectionType: isSome(item)
          ? convertShapeNameForDisplay(shapeNameConversionMap, 'productName', item.sectionType)
          : '',
        steelType: item?.steelType ?? '',
        size: isSome(item) ? convertShapeNameForDisplay(shapeNameConversionMap, 'size', item.size) : '',
        thicknessDiameter: item?.thicknessDiameter ?? null,
        length: isSome(item) ? convertShapeNameForDisplay(shapeNameConversionMap, 'length', item.length) : '',
        quantity: item?.quantity.toString() ?? '',
        weight: item?.weightBeforeProcessing?.toString() ?? '',
        heatNumber: item?.heatNumber ?? '',
        individualNumber: item?.individualNumber ?? '',
        no: millsheet.no,
        manufacturerName: millsheet.manufacturerName,
        // 工区・節・部位は明細ごとの属性（親ミルシートの値は集約表示のため使わない）
        constructionArea: item?.constructionArea ?? '',
        member: item?.member ?? '',
        buildingPart: item?.buildingPart ?? '',
        memo: '',
        pageNumber: (() => {
          const plannedPageNumber = pageNumberByMillsheetId?.get(millsheet.id)
          if (typeof plannedPageNumber === 'number') {
            return millsheet.millsheetItems.length <= 1
              ? `${plannedPageNumber}`
              : `${plannedPageNumber}-${itemIndex + 1}`
          }
          return plannedPageNumber ?? numberings[millsheetIndex]?.rowLabels[itemIndex] ?? ''
        })(),
        tradingCompany: millsheet.tradingCompany,
        orderNumber: millsheet.orderNumber,
        innerNo: millsheet.innerNo,
        endorsementCompany: item?.endorsementCompany ?? null,
        customer: millsheet.customer,
        referenceNumber,
        supplierName,
      },
    }))
  })
}

export async function downloadSteelOrderCsv(rows: SteelOrderExportRow[]): Promise<void> {
  const [{ stringify }, { saveAs }] = await Promise.all([import('csv-stringify/sync'), import('file-saver')])
  // プレビュー（csvExportPreviewColumns）と実ダウンロードの列がズレないよう、同じ定義から組み立てる
  const headers = CSV_COLUMNS.map((column) => column.label)
  const data = rows.map((row) => CSV_COLUMNS.map((column) => column.getValue(row)))
  const csv = stringify([headers, ...data], { bom: true, escape_formulas: true })
  saveAs(new Blob([csv], { type: 'text/csv' }), `発注ミルシート明細一覧_${dayjs().format('YYYYMMDDHHmmss')}.csv`)
}
